import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Avatar from 'flarum/common/components/Avatar';
import UserCard from 'flarum/forum/components/UserCard';
import UserPage from 'flarum/forum/components/UserPage';
import Group from 'flarum/common/models/Group';
import Link from 'flarum/common/components/Link';
import CommentPost from 'flarum/forum/components/CommentPost';
import { visibleNumber } from './utils/postNumbers';

/** Two-letter initials as on the portal ("Анна Морозова" → "АМ", "alice" → "AL"). */
function initials(name) {
  const words = name.trim().split(/[\s._-]+/).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[1][0] : name.trim().slice(0, 2);
  return letters.toUpperCase();
}

/** «Title», unless the title already opens with a guillemet (no ««…»). */
const quoted = title => /^[«"„]/.test(title.trim()) ? title : `«${title}»`;
export default function addProfile() {
  // Users without an uploaded photo: initials instead of a single letter on a random colour.
  override(Avatar.prototype, 'view', function (original, vnode) {
    const result = original(vnode);
    const user = vnode.attrs.user;
    if (!user || user.avatarUrl() || !result || result.tag !== 'span') return result;
    const {
      style,
      ...attrs
    } = result.attrs || {};
    return m("span", attrs, initials(user.displayName() || '?'));
  });
  extend(UserCard.prototype, 'infoItems', function (items) {
    const user = this.attrs.user;

    // Special role only (Admin, Mod…): members get no label.
    const roles = (user.groups() || []).filter(group => group && group.id() !== Group.MEMBER_ID);
    if (roles.length) {
      items.add('tp-role', m("span", {
        className: "TpRole"
      }, roles.map(group => group.nameSingular()).join(' · ')), 120);
    }

    // "Участник с 5 октября 2026" instead of a relative "joined 3 days ago".
    if (items.has('joined') && user.joinTime()) {
      items.setContent('joined', app.translator.trans('tarotportal-theme.forum.user.member_since', {
        date: new Intl.DateTimeFormat(app.data.locale || 'ru', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }).format(user.joinTime()).replace(/\s?г\.$/, '')
      }));
    }
  });

  // Activity list item: "В «Тема»" as the item's own head, with the post number on the right
  override('flarum/forum/components/PostListItem', 'view', function () {
    const post = this.attrs.post;
    return m("div", {
      className: "PostListItem TpActivityItem"
    }, m("div", {
      className: "PostListItem-discussion TpActivityItem-head"
    }, m(Link, {
      href: app.route.post(post)
    }, app.translator.trans('tarotportal-theme.forum.user.in_discussion', {
      title: quoted(post.discussion().title())
    })), m("span", {
      className: "TpPostNumber"
    }, String(visibleNumber(post)).padStart(2, '0'))), m(CommentPost, {
      post: post,
      params: this.attrs.params
    }));
  });

  // Compact page heading over the activity lists: "СООБЩЕНИЯ · 3 сообщения", "ТЕМЫ · 2 темы"
  const listHeading = (key, count) => m("div", {
    className: "TpListTitle"
  }, m("span", {
    className: "TpEyebrow"
  }, app.translator.trans(`tarotportal-theme.forum.user.${key}_heading`)), m("span", {
    className: "TpMeta"
  }, app.translator.trans(`tarotportal-theme.forum.user.${key}_count`, {
    count
  })));
  extend('flarum/forum/components/PostsUserPage', 'content', function (vnode) {
    if (vnode && Array.isArray(vnode.children) && this.user) vnode.children.unshift(listHeading('posts', this.user.commentCount() || 0));
  });
  extend('flarum/forum/components/DiscussionsUserPage', 'content', function (vnode) {
    if (vnode && Array.isArray(vnode.children) && this.user) vnode.children.unshift(listHeading('discussions', this.user.discussionCount() || 0));
  });

  // Profile stays minimal: replies, discussions (both with counts), own settings and security.
  extend(UserPage.prototype, 'navItems', function (items) {
    ['likes', 'mentions'].forEach(key => items.has(key) && items.remove(key));
  });
}